import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { createBillingAccessApiService } from '../services/BillingAccessApiService';
import type { BillingAccessSnapshot, BillingAccount } from '../types';

export interface UseBillingAccessResult {
  account: BillingAccount | null;
  access: BillingAccessSnapshot | null;
  loading: boolean;
  error: string | null;
  refresh: (force?: boolean) => Promise<void>;
  recompute: () => Promise<void>;
  can: (capability: string) => boolean;
}

const ACCESS_CACHE_TTL_MS = 60_000;

interface BillingAccessCacheEntry {
  account: BillingAccount | null;
  access: BillingAccessSnapshot | null;
  expiresAt: number;
  promise?: Promise<{ account: BillingAccount; access: BillingAccessSnapshot }>;
}

const billingAccessCache = new Map<string, BillingAccessCacheEntry>();

export function clearBillingAccessCache(projectId?: string): void {
  if (!projectId) {
    billingAccessCache.clear();
    return;
  }
  for (const key of Array.from(billingAccessCache.keys())) {
    if (key.endsWith(`:${projectId}`)) {
      billingAccessCache.delete(key);
    }
  }
}

function matchesCapability(granted: string, requested: string): boolean {
  if (granted === requested) return true;
  if (granted.endsWith('.*')) {
    return requested.startsWith(granted.slice(0, -1));
  }
  return false;
}

export function useBillingAccess(
  projectId: string | undefined,
  options: { disabled?: boolean } = {},
): UseBillingAccessResult {
  const service = useMemo(() => createBillingAccessApiService(), []);
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [access, setAccess] = useState<BillingAccessSnapshot | null>(null);
  const [loading, setLoading] = useState(Boolean(projectId));
  const [error, setError] = useState<string | null>(null);

  const cacheKey = useMemo(() => {
    if (!projectId) return null;
    const uid = getAuth().currentUser?.uid || 'anonymous';
    return `${uid}:${projectId}`;
  }, [projectId]);

  const refresh = useCallback(async (force = false) => {
    if (!projectId || options.disabled) {
      setAccount(null);
      setAccess(null);
      setLoading(false);
      return;
    }
    if (!cacheKey) return;
    setLoading(true);
    setError(null);
    try {
      const cached = billingAccessCache.get(cacheKey);
      if (!force && cached && cached.expiresAt > Date.now() && cached.account && cached.access) {
        setAccount(cached.account);
        setAccess(cached.access);
        return;
      }
      const promise = cached?.promise ?? Promise.all([
        service.getAccount(projectId),
        service.getAccessSnapshot(projectId),
      ]).then(([nextAccount, nextAccess]) => ({
        account: nextAccount,
        access: nextAccess,
      }));
      billingAccessCache.set(cacheKey, {
        account: cached?.account ?? null,
        access: cached?.access ?? null,
        expiresAt: cached?.expiresAt ?? 0,
        promise,
      });
      const next = await promise;
      billingAccessCache.set(cacheKey, {
        ...next,
        expiresAt: Date.now() + ACCESS_CACHE_TTL_MS,
      });
      setAccount(next.account);
      setAccess(next.access);
    } catch (err) {
      setError((err as Error).message);
      billingAccessCache.delete(cacheKey);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, options.disabled, projectId, service]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const recompute = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const nextAccess = await service.recomputeAccessSnapshot(projectId);
      const nextAccount = await service.getAccount(projectId);
      setAccess(nextAccess);
      setAccount(nextAccount);
      if (cacheKey) {
        billingAccessCache.set(cacheKey, {
          access: nextAccess,
          account: nextAccount,
          expiresAt: Date.now() + ACCESS_CACHE_TTL_MS,
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, projectId, service]);

  const can = useCallback((capability: string) => {
    if (!access) return false;
    return Object.entries(access.capabilities).some(([granted, value]) =>
      value.allowed && matchesCapability(granted, capability),
    );
  }, [access]);

  return {
    account,
    access,
    loading,
    error,
    refresh,
    recompute,
    can,
  };
}
